import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  UserCircle,
  PencilSimple,
  Check,
  X,
  Trophy,
  Diamond,
} from "@phosphor-icons/react";
import PremiumBadge from "@/components/PremiumBadge";
import {
  changeName,
  getProfile,
  purchasePremium,
  formatApiError,
} from "@/lib/api";
import { toast } from "sonner";

function fmtExpiry(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString();
}

export default function ProfileDialog({ open, onOpenChange, onUserUpdated, onPreferencesUpdated }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  // ---- Load fresh profile every time the dialog opens ----
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const p = await getProfile();
      setProfile(p);
      setDraftName(p.user?.name || "");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load profile");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (open) {
      setEditingName(false);
      refresh();
    }
  }, [open, refresh]);

  // ---- Username edit ----
  const handleSaveName = useCallback(async () => {
    const next = draftName.trim();
    if (!next) {
      toast.error("Name cannot be empty");
      return;
    }
    if (profile && next === profile.user.name) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      const updated = await changeName(next);
      setProfile((p) => ({ ...p, user: updated }));
      onUserUpdated?.(updated);
      setEditingName(false);
      toast.success("Username updated");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not update");
    } finally {
      setSavingName(false);
    }
  }, [draftName, profile, onUserUpdated]);

  // ---- Premium purchase ----
  const handlePurchasePremium = useCallback(async () => {
    if (purchasing) return;
    setPurchasing(true);
    try {
      const r = await purchasePremium();
      toast.success("Premium activated for 30 days!");
      // Refresh profile to pick up new premium_until + updated gem balance
      await refresh();
      // Notify parent so HUD gem count refreshes
      onUserUpdated?.({ ...profile?.user, _gems_changed: r.current_gems });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not purchase");
    } finally {
      setPurchasing(false);
    }
  }, [purchasing, refresh, profile, onUserUpdated]);

  if (!open) return null;

  const rank = profile?.rank;
  const isPremium = !!profile?.is_premium;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="profile-dialog"
        className="rounded-none border border-white/15 bg-[#0A0E14] text-white max-w-xl p-0"
      >
        <DialogHeader className="px-5 py-4 border-b border-white/10">
          <DialogTitle className="mono uppercase tracking-widest text-[12px] text-zinc-300 flex items-center gap-2">
            <UserCircle size={14} /> Profile
          </DialogTitle>
        </DialogHeader>

        <div className="px-5 py-4 space-y-5 max-h-[75vh] overflow-y-auto">
          {loading || !profile ? (
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">
              Loading…
            </div>
          ) : (
            <>
              {/* ---- Username row ---- */}
              <section>
                <h3 className="mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2">
                  Username
                </h3>
                {editingName ? (
                  <div className="flex items-center gap-1.5">
                    <Input
                      data-testid="profile-name-input"
                      autoFocus
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveName();
                        if (e.key === "Escape") setEditingName(false);
                      }}
                      maxLength={40}
                      className="rounded-none border-emerald-400/50 bg-black/40 mono tracking-wider focus-visible:ring-emerald-500/30"
                    />
                    <button
                      type="button"
                      data-testid="profile-name-save"
                      disabled={savingName}
                      onClick={handleSaveName}
                      title="Save"
                      className="border border-emerald-400/40 text-emerald-400 hover:bg-emerald-400/10 p-2 disabled:opacity-40"
                    >
                      <Check size={13} weight="bold" />
                    </button>
                    <button
                      type="button"
                      data-testid="profile-name-cancel"
                      onClick={() => {
                        setDraftName(profile.user.name);
                        setEditingName(false);
                      }}
                      title="Cancel"
                      className="border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 p-2"
                    >
                      <X size={13} weight="bold" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 border border-white/10 bg-black/40 px-3 py-2">
                    {isPremium && <PremiumBadge size={14} />}
                    <span className="mono tracking-wider text-sm flex-1 truncate">
                      {profile.user.name}
                    </span>
                    <button
                      type="button"
                      data-testid="profile-name-edit-btn"
                      onClick={() => setEditingName(true)}
                      title="Rename"
                      className="text-zinc-500 hover:text-emerald-400 transition-colors"
                    >
                      <PencilSimple size={13} />
                    </button>
                  </div>
                )}
                <div className="mono text-[9px] uppercase tracking-widest text-zinc-600 mt-1">
                  {profile.user.email}
                </div>
              </section>

              {/* ---- Rank progress ---- */}
              <section>
                <h3 className="mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-1.5">
                  <Trophy size={11} /> Rank Progress
                </h3>
                <div className="border border-amber-400/30 bg-amber-400/[0.03] p-3">
                  <div className="flex items-baseline justify-between mb-1">
                    <span
                      className="font-bold text-base tracking-tight text-amber-300"
                      data-testid="profile-rank-label"
                    >
                      {rank?.label || "—"}
                    </span>
                    <span className="mono text-[10px] uppercase tracking-widest text-zinc-500">
                      <span data-testid="profile-xp">{(profile.xp || 0).toLocaleString()}</span> XP
                    </span>
                  </div>
                  {rank?.max_tier ? (
                    <div className="mono text-[10px] uppercase tracking-widest text-amber-200 mt-1">
                      Max rank reached
                    </div>
                  ) : (
                    <>
                      <div className="h-1.5 bg-white/5 overflow-hidden">
                        <div
                          className="h-full bg-amber-400"
                          style={{ width: `${Math.round((rank?.progress_to_next || 0) * 100)}%` }}
                          data-testid="profile-rank-bar"
                        />
                      </div>
                      <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                        {Math.max(0, (rank?.xp_for_next ?? 0) - (profile.xp || 0)).toLocaleString()}{" "}
                        XP to <span className="text-amber-300">{rank?.next_label}</span>
                      </div>
                    </>
                  )}
                </div>
              </section>

              {/* ---- Premium status ---- */}
              <section>
                <h3 className="mono text-[10px] uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-1.5">
                  <PremiumBadge size={11} /> Premium Membership
                </h3>
                {isPremium ? (
                  <div className="border border-amber-400/40 bg-amber-400/[0.05] p-3">
                    <div className="flex items-center gap-3 mb-3">
                      <PremiumBadge size={28} />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-amber-200 text-sm">Active</div>
                        <div className="mono text-[10px] uppercase tracking-widest text-zinc-500" data-testid="profile-premium-until">
                          Expires {fmtExpiry(profile.premium_until)}
                        </div>
                      </div>
                      <Button
                        data-testid="profile-premium-extend-btn"
                        onClick={handlePurchasePremium}
                        disabled={purchasing}
                        size="sm"
                        className="rounded-none h-8 mono uppercase text-[10px] tracking-widest bg-amber-400 hover:bg-amber-300 text-black"
                      >
                        <Diamond size={11} weight="fill" className="mr-1" />
                        +30 days · 500
                      </Button>
                    </div>
                    <ul className="mono text-[10px] uppercase tracking-widest text-amber-200/80 space-y-1 border-t border-amber-400/20 pt-2">
                      <li>· Gold fire-helmet badge next to your name (HUD, chat, mutual aid)</li>
                      <li>· Faster call spawn intervals (15s, 30s, or 60s)</li>
                      <li>· Auto-Transport for ambulances — Closest or Most Capable hospital, works on your own AND alliance shared calls</li>
                    </ul>
                  </div>
                ) : (
                  <div className="border border-white/10 bg-white/[0.02] p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <PremiumBadge size={18} />
                      <span className="text-sm font-bold text-zinc-200">Become a Premium Member</span>
                    </div>
                    <ul className="mono text-[10px] uppercase tracking-widest text-zinc-500 space-y-1 mb-3">
                      <li>· Gold fire-helmet badge next to your name (HUD, chat, mutual aid)</li>
                      <li>· Faster call spawn intervals (15s or 30s instead of 60s)</li>
                      <li>· <span className="text-amber-200">NEW</span> · Auto-Transport for ambulances (Closest or Most Capable hospital) — works on your own AND alliance shared calls</li>
                      <li>· Visible to all alliance members</li>
                    </ul>
                    <Button
                      data-testid="profile-premium-buy-btn"
                      onClick={handlePurchasePremium}
                      disabled={purchasing}
                      size="sm"
                      className="w-full rounded-none h-9 mono uppercase text-[11px] tracking-widest bg-amber-400 hover:bg-amber-300 text-black disabled:opacity-50"
                    >
                      <Diamond size={12} weight="fill" className="mr-1.5" />
                      Buy 30 Days · 500 Gems
                    </Button>
                  </div>
                )}
              </section>

            </>
          )}

          <div className="pt-2 flex justify-end">
            <Button
              data-testid="profile-close-btn"
              onClick={() => onOpenChange(false)}
              size="sm"
              variant="outline"
              className="rounded-none h-8 mono uppercase text-[10px] tracking-widest border-white/15 text-zinc-300 hover:bg-white/5"
            >
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
